"use client";

import { useCallback, useContext, useState } from "react";
import { useShellRouter } from "@/src/hooks";
import { useSnapshot } from "valtio";
import { deviceDetector } from "@ospweb/sdk-ui";
import log from "logbeacon";
import { getDeviceType, getGaInfoForReporting } from "utils";
import { sendGtmOrApp } from "../utils/sendGtmOrApp";
import { getBridgeErrorToastMessage } from "./useAppPurchaseFlowMessages";
import Translations from "@translate/hooks/useAppPurchaseFlow";
import { I18nUtil } from "@utils/i18n";
import type {
  MemberProduct,
  OfferDetail,
  OfferDetailSign,
  PaymentCheckResponseData,
} from "@/src/features/api/memberApi";
import { PayChannel } from "@/src/features/api/memberApi";
import { OspApiClientContext } from "@/src/features";
import { mergeAdjustAdidIntoHeaders } from "@/src/lib/bridge/adjustRequestHeaders";
import { getMemberApi } from "@/src/services/helper";
import { useAppPayment } from "@/src/lib/bridge";
import { useAppFeatures } from "@/src/lib/bridge/useBridge";
import type { BridgeRequestError } from "@/src/lib/bridge/bridge";
import { rootStore } from "@/src/store";
import type { PayChannelFilter } from "@/src/utils";

const I18n = I18nUtil.createScoped(Translations);

const ACTIVE = "ACTIVE";

/** paymentCheck 接口异常码 */
const PAYMENT_CHECK_CODE = {
  USER_MEMBER_EXIST: 186008,
  INVALID_PARAMETER: 180001,
} as const;

function normalizeNativePaymentErrorMessage(message: string): string {
  const msg = String(message || "").trim();
  if (!msg) {
    return I18n.t("Purchase failed.");
  }

  // Android Google Play Billing：部分设备/系统/Play 版本不支持 ProductDetails API
  if (
    deviceDetector.isAndroidApp() &&
    /does not support ProductDetails|ProductDetails/i.test(msg)
  ) {
    return I18n.t(
      "Your device/OS doesn't support in-app purchases. Please upgrade your system or update Google Play and try again.",
    );
  }

  return msg;
}

/**
 * 根据 paymentCheck 返回的 code/msg 解析用户提示文案
 */
export function getPaymentCheckErrorMessage(res: {
  code?: number | string;
  msg?: string;
}): string {
  const code = typeof res?.code === "string" ? Number(res.code) : res?.code;
  const msg = res?.msg;
  if (
    code === PAYMENT_CHECK_CODE.USER_MEMBER_EXIST ||
    msg === "USER_MEMBER_EXIST"
  ) {
    return I18n.t("Member already exists.");
  }
  if (
    code === PAYMENT_CHECK_CODE.INVALID_PARAMETER ||
    msg === "INVALID_PARAMETER"
  ) {
    return I18n.t("Purchase failed.");
  }
  return I18n.t("Purchase failed.");
}

function isResponseLike(error: unknown): error is Response {
  const e = error as {
    status?: unknown;
    statusText?: unknown;
    clone?: unknown;
    headers?: unknown;
  };
  return !!(
    e &&
    typeof e === "object" &&
    typeof e.status === "number" &&
    typeof e.statusText === "string" &&
    typeof e.clone === "function"
  );
}

async function getMessageFromResponse(
  res: Response,
): Promise<string | undefined> {
  // 优先尝试解析 JSON（兼容后端 {code,msg} 或 {message} 结构）
  try {
    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const json = (await res.clone().json()) as unknown;
      const maybePaymentBody = json as { code?: number; msg?: string };
      if (maybePaymentBody?.code != null || maybePaymentBody?.msg) {
        return getPaymentCheckErrorMessage(maybePaymentBody);
      }
      const maybeMessage = json as {
        message?: unknown;
        error?: unknown;
        msg?: unknown;
      };
      if (typeof maybeMessage?.message === "string" && maybeMessage.message) {
        return maybeMessage.message;
      }
      if (typeof maybeMessage?.msg === "string" && maybeMessage.msg) {
        return maybeMessage.msg;
      }
      if (typeof maybeMessage?.error === "string" && maybeMessage.error) {
        return maybeMessage.error;
      }
    }
  } catch {
    // ignore
  }

  // 再尝试解析 text（避免直接 toString() -> [object Response]）
  try {
    const text = await res.clone().text();
    if (text) {
      return text;
    }
  } catch {
    // ignore
  }

  if (res.statusText) {
    return res.statusText;
  }
  if (Number.isFinite(res.status)) {
    return `Request failed (${res.status})`;
  }
  return undefined;
}

export interface AppPurchaseFlowOptions {
  /** 购买成功后的回调（如刷新、Toast） */
  onSuccess?: () => void | Promise<void>;
  /**
   * 购买失败/取消时的回调。
   * 传入的 `message` 已由本 hook 完成 **Bridge 错误码映射**、默认英文提示与设备兼容文案处理，
   * **请勿**再当作原生调试原文二次展示到其它渠道；详细 `errMsg` 见 log。
   */
  onError?: (message: string) => void;
  /** paymentCheck 返回会员已存在时触发，具体刷新逻辑由外部决定 */
  onMemberExist?: () => void | Promise<void>;
  /**
   * 为 true 时：若 `message` 包含 `timeout`（不区分大小写）则**不调用** `onError`，
   * 用于避免 Bridge 请求超时与后续 Stripe 降级重复打扰。
   */
  suppressTimeoutToast?: boolean;
}

/** 统一走 `onError`，并支持可选的 timeout 静默 */
function invokePurchaseFlowError(
  options: AppPurchaseFlowOptions | undefined,
  message: string,
): void {
  const cb = options?.onError;
  if (!cb) {
    return;
  }
  if (
    options?.suppressTimeoutToast &&
    String(message || "")
      .toLowerCase()
      .includes("timeout")
  ) {
    return;
  }
  cb(message);
}

export interface AppPurchaseFlowResult {
  success: boolean;
  error?: string;
  /**
   * 建议降级到 H5/Stripe（仅在判断为“原生支付桥接/能力不可用”时返回 true）
   * 注意：不用于用户取消/支付失败等业务错误场景
   */
  fallbackToStripe?: boolean;
}

/**
 * App 内购完整流程：从产品取 storeProductId → fetchProducts → paymentCheck → purchase → confirm 由 useAppPayment 内部处理。
 *
 * **`purchaseInApp`**：错误提示文案在 hook 内统一生成（含 `BridgeErrorCode` → 用户短句）；调用方通过 `onError` 决定如何展示（Toast 等），可选 `suppressTimeoutToast` 避免超时类重复打扰。
 */
export function useAppPurchaseFlow() {
  const router = useShellRouter();
  const { memberApi } = useContext(OspApiClientContext);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const { purchase, fetchProducts } = useAppPayment();
  const [isPurchasing, setIsPurchasing] = useState(false);

  const isApp = deviceDetector.isApp();
  const appFeatures = useAppFeatures();
  const supportsNativePayment = appFeatures.nativePayment === true;
  const canUseNativePayment = isApp && supportsNativePayment; // 保持原语义：仅“声明支持”才为 true
  const shouldTryNativePayment = isApp; // 新语义：在 App 内就优先尝试原生支付（防止 features 注入缺失导致误降级）
  const payChannel: PayChannelFilter = deviceDetector.isIOSApp()
    ? PayChannel.APPLE_IAP
    : PayChannel.GOOGLE_IAP;

  const isLikelyNativePaymentNotSupported = (message: string): boolean => {
    const m = String(message || "").toLowerCase();
    // Bridge/协议层常见失败：无 handler/无实现/请求超时/未知消息
    return (
      m.includes("request timeout") ||
      m.includes("bridge") ||
      m.includes("handler") ||
      m.includes("not implemented") ||
      m.includes("unknown message") ||
      m.includes("not supported in this app version")
    );
  };

  const sendAppPurchaseEvent = useCallback(
    (productForPurchase: MemberProduct) => {
      const eventData = {
        event: "begin_checkout",
        ecommerce: {
          value: productForPurchase.price, // 结账单的总价值
          currency: "USD", // 货币
          items: [
            // 必须包含用户打算购买的商品明细
            {
              item_id: productForPurchase.id,
              item_name: productForPurchase.name,
              price: productForPurchase.price,
              quantity: 1,
            },
          ],
        },
      };
      sendGtmOrApp(eventData);
    },
    [],
  );

  /**
   * 发起 App 内购全流程。失败时通过 `options.onError` 传出**已映射**的用户可见短文案；见 {@link AppPurchaseFlowOptions}。
   */
  const purchaseInApp = useCallback(
    async (
      productForPurchase: MemberProduct,
      options?: AppPurchaseFlowOptions,
    ): Promise<AppPurchaseFlowResult> => {
      if (!isApp) {
        const msg = I18n.t("Payment is only available in App environment.");
        invokePurchaseFlowError(options, msg);
        return { success: false, error: msg };
      }

      const backendProductId = productForPurchase?.id ?? null;
      log.debug(
        "[Member][trace][pay] purchaseInApp backendProductId",
        backendProductId,
      );
      if (!backendProductId) {
        const msg = I18n.t("No product ID found.");
        invokePurchaseFlowError(options, msg);
        return { success: false, error: msg };
      }

      const paymentConfig = productForPurchase?.payment_configs?.find(
        (c) => c.channel === payChannel && c.status === ACTIVE,
      );
      const storeProductId = paymentConfig?.product_id ?? null;
      if (!storeProductId) {
        const msg = I18n.t(
          "This product is not available for in-app purchase.",
        );
        // App 内：产品缺少 IAP 配置时，允许直接回退 Stripe/H5
        // （例如：后台只配置了 Stripe，或 IAP 尚未上架/未激活）
        return { success: false, error: msg, fallbackToStripe: true };
      }

      setIsPurchasing(true);
      sendAppPurchaseEvent(productForPurchase);
      try {
        // 即使 features.nativePayment 未注入/为 false，也先尝试 fetchProducts 做能力探测；
        // 仅在确认原生桥接不可用时再建议回退到 Stripe/H5。
        // 目前 lifetime 产品是一次性产品，所以需要使用 INAPP 类型，其他产品都是订阅类型，使用 SUBS 类型。
        try {
          await fetchProducts([storeProductId], {
            productType:
              productForPurchase.pay_cycle === "LIFETIME" ? "INAPP" : "SUBS",
          });
        } catch (e) {
          const maybeBridgeError = e as BridgeRequestError | undefined;
          const bridgeCode = maybeBridgeError?.bridgeError?.code;
          let msg: string;
          if (bridgeCode) {
            msg = normalizeNativePaymentErrorMessage(
              getBridgeErrorToastMessage(bridgeCode),
            );
            log.error("[Member][trace][pay] fetchProducts failed (bridge)", {
              code: bridgeCode,
              errMsg: maybeBridgeError?.bridgeError?.errMsg,
              error: e,
            });
          } else {
            msg = normalizeNativePaymentErrorMessage(
              e instanceof Error ? e.message : String(e),
            );
            log.error("[Member][trace][pay] fetchProducts failed", {
              message: msg,
              error: e,
            });
          }
          const shouldFallback =
            !supportsNativePayment && isLikelyNativePaymentNotSupported(msg);
          if (shouldFallback) {
            log.warn(
              "[Member][trace][pay] Native payment flag missing but bridge seems unavailable; fallback to Stripe suggested",
              { msg, supportsNativePayment },
            );
            // 不触发 onError（避免 toast），交由上层直接走 Stripe
            return { success: false, error: msg, fallbackToStripe: true };
          }
          invokePurchaseFlowError(options, msg);
          return { success: false, error: msg };
        }

        // 优先使用 Context 的 memberApi（带 securityWorker/authModule 登录态），否则兜底 getMemberApi()
        const api = memberApi ?? (await getMemberApi());
        if (!api?.payment?.paymentCheck) {
          const msg = I18n.t("Purchase failed.");
          invokePurchaseFlowError(options, msg);
          return { success: false, error: msg };
        }

        const gaInfo = await getGaInfoForReporting();

        const checkRes = await api.payment.paymentCheck(
          {
            product_id: backendProductId,
            pay_channel: payChannel as PayChannel,
          },
          {
            headers: mergeAdjustAdidIntoHeaders({
              "ga-instance-id": gaInfo.gaInstanceId || "",
              "ga-client-id": gaInfo.clientId || "",
              "ga-session-id": gaInfo.sessionId || "",
              "os-device-type": getDeviceType(),
            }),
          },
        );

        // 按实际返回结构（以日志为准）：
        // checkRes = { data: { code, msg, data }, error?: any }
        // 其中真正的优惠在：checkRes.data.data.offer_details / owner_external_ref
        const checkOuter = checkRes as {
          data?: {
            code?: number;
            msg?: string;
            data?: PaymentCheckResponseData;
          };
          error?: unknown;
        };
        const checkBody = checkOuter?.data;

        const code = checkBody?.code;
        const msg = checkBody?.msg;
        // paymentCheck 接口异常：code 186008 会员已存在，180001 参数错误，其余失败兜底提示购买失败
        const isCheckError =
          code === PAYMENT_CHECK_CODE.USER_MEMBER_EXIST ||
          code === PAYMENT_CHECK_CODE.INVALID_PARAMETER ||
          msg === "USER_MEMBER_EXIST" ||
          msg === "INVALID_PARAMETER" ||
          (typeof code === "number" && code !== 0 && code !== 200);
        if (isCheckError) {
          if (
            code === PAYMENT_CHECK_CODE.USER_MEMBER_EXIST ||
            msg === "USER_MEMBER_EXIST"
          ) {
            await options?.onMemberExist?.();
          }
          const errMsg = getPaymentCheckErrorMessage({ code, msg });
          invokePurchaseFlowError(options, errMsg);
          return { success: false, error: errMsg };
        }
        // checkRes.data.data 返回格式:
        // { offer_details: [{ eligible, code, sign: { key_id, nonce, signature, timestamp } }], owner_external_ref, product_id }
        const checkInner = checkBody?.data;
        log.error("[Member][trace][pay] checkBody", JSON.stringify(checkBody));

        const offersList: OfferDetail[] = checkInner?.offer_details ?? [];
        const ownerExternalRef = checkInner?.owner_external_ref;
        const oldPurchaseToken = checkInner?.old_purchase_token;

        const purchaseOptions: Parameters<typeof purchase>[1] = {
          obfuscatedAccountId: ownerInfo?.id ?? undefined,
          backendProductId,
        };

        // sign 完整：API 返回 key_id, nonce, signature, timestamp（均为 snake_case）
        const hasCompleteSign = (sign?: OfferDetailSign) => {
          return !!(
            sign?.key_id &&
            sign?.nonce &&
            sign?.signature &&
            String(sign.signature).length > 0 &&
            sign?.timestamp != null
          );
        };

        const firstEligibleOfferWithSign = offersList.find(
          (o: OfferDetail) => o.eligible === true && hasCompleteSign(o.sign),
        );

        if (payChannel === PayChannel.APPLE_IAP) {
          // iOS：仅促销优惠需要完整签名；owner_external_ref（appAccountToken）只要有就应传
          if (ownerExternalRef) {
            purchaseOptions.ownerExternalRef = ownerExternalRef;
          }
          if (firstEligibleOfferWithSign) {
            purchaseOptions.offerDetails = [firstEligibleOfferWithSign];
            log.error(
              "[Member][trace][pay] iOS 已携带促销优惠: code=",
              firstEligibleOfferWithSign.code,
              "owner_external_ref=",
              purchaseOptions.ownerExternalRef ?? "nil",
            );
          } else {
            log.error(
              "[Member][trace][pay] iOS 未携带促销优惠: offersList.length=",
              offersList.length,
              "firstEligibleWithSign=",
              !!firstEligibleOfferWithSign,
              "ownerExternalRef=",
              ownerExternalRef ?? "nil",
            );
          }
        } else {
          // Android：offer_details 通常无 sign；优惠以 offer code 表达（用于原生侧匹配 offerId/offerTags 进而拿到 offerToken）
          const firstEligibleOffer = offersList.find(
            (o: OfferDetail) => o.eligible === true && !!o.code,
          );
          if (firstEligibleOffer?.code) {
            purchaseOptions.offerToken = firstEligibleOffer.code;
            log.debug(
              "[Member][trace][pay] Android 已携带优惠: offerCode=",
              firstEligibleOffer.code,
            );
          } else {
            log.debug(
              "[Member][trace][pay] Android 未携带优惠: offersList.length=",
              offersList.length,
            );
          }
        }
        // Android：传 planId（仅来自产品 payment_config.plan_id），原生无 offerToken 时按 plan_id 匹配方案下单
        if (payChannel === PayChannel.GOOGLE_IAP && paymentConfig?.plan_id) {
          purchaseOptions.planId = paymentConfig.plan_id;
        }
        if (payChannel === PayChannel.GOOGLE_IAP && oldPurchaseToken) {
          purchaseOptions.oldPurchaseToken = oldPurchaseToken;
        }

        log.debug(
          "[Member][trace][pay] App purchase started",
          backendProductId,
          storeProductId,
          JSON.stringify(purchaseOptions),
        );
        const result = await purchase(storeProductId, purchaseOptions);

        if (result.success) {
          log.info(
            "[Member][trace][pay] App purchase successful",
            JSON.stringify(result),
            options,
          );
          await options?.onSuccess?.();
          // 默认刷新页面，若调用方传了 onSuccess 则不再自动 refresh
          if (!options?.onSuccess) {
            setTimeout(() => router.refresh(), 1000);
          }
          return { success: true };
        }

        log.error(
          "[Member][trace][pay] App purchase failed",
          JSON.stringify(result.error),
        );
        // iOS/原生部分兜底场景可能拿不到映射 code。
        // 按需求：toast 使用默认提示，detail 文案只保留在 error log 中。
        const errMsgRaw = getBridgeErrorToastMessage(result.errorCode);
        const errMsg = normalizeNativePaymentErrorMessage(errMsgRaw);
        invokePurchaseFlowError(options, errMsg);
        return { success: false, error: errMsg };
      } catch (error) {
        // native bridge 结构化错误（purchase/restore/status/request 等）：
        // useAppPayment 在失败时会 reject Error，但会把 bridgeError code 挂在 Error 上，
        // 因此这里优先走 code -> toast 映射，保证文案稳定且与错误码一致。
        const maybeBridgeError = error as BridgeRequestError | undefined;
        const bridgeCode = maybeBridgeError?.bridgeError?.code;
        if (bridgeCode) {
          const toast = normalizeNativePaymentErrorMessage(
            getBridgeErrorToastMessage(bridgeCode),
          );
          invokePurchaseFlowError(options, toast);
          return { success: false, error: toast };
        }

        // paymentCheck 或其它接口抛错时，尝试从响应体解析 code/msg
        const errBody =
          (error as { body?: { code?: number; msg?: string } })?.body ??
          (error as { response?: { data?: { code?: number; msg?: string } } })
            ?.response?.data ??
          (error as { code?: number; msg?: string });
        const errorMessageRaw =
          errBody && (errBody.code != null || errBody.msg != null)
            ? getPaymentCheckErrorMessage(errBody)
            : isResponseLike(error)
              ? ((await getMessageFromResponse(error)) ?? "Purchase failed")
              : error instanceof Error
                ? error.message
                : typeof error === "string"
                  ? error
                  : (() => {
                      try {
                        const s = JSON.stringify(error);
                        return s && s !== "{}" ? s : I18n.t("Purchase failed.");
                      } catch {
                        return I18n.t("Purchase failed.");
                      }
                    })();
        const errorMessage =
          normalizeNativePaymentErrorMessage(errorMessageRaw);
        log.error("[Member][trace][pay] App purchase error", {
          message: errorMessage,
          error,
        });
        invokePurchaseFlowError(options, errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsPurchasing(false);
      }
    },
    [
      isApp,
      payChannel,
      memberApi,
      ownerInfo?.id,
      purchase,
      fetchProducts,
      router,
      sendAppPurchaseEvent,
      supportsNativePayment,
    ],
  );

  return {
    purchaseInApp,
    isPurchasing,
    /** 是否 App 环境（不代表一定支持原生支付） */
    isApp,
    /** 原生端是否声明支持原生支付（IAP/Google Billing） */
    supportsNativePayment,
    /** 是否满足“在 App 内且支持原生支付” */
    canUseNativePayment,
    /** 在 App 内优先尝试原生支付（即使 features 注入缺失） */
    shouldTryNativePayment,
    payChannel,
  };
}
